import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree, CanActivateFn } from '@angular/router';
import { UserService } from '../services/user_service';
import { NoteService } from '../services/note_service';
import { Note } from '../types/note';

const checkPermissions = (note: Note, noteState: string, userService: UserService): Promise<boolean> => {
    if (userService.isLoggedIn()) {
        return userService.getProfile()
            .then(user => {
                const isNoteOwner = (user.id && user.id.toString() === note.userId.toString());
                return (noteState === 'HIDDEN') ? isNoteOwner : true;
            });
    } else {
        return Promise.resolve(noteState !== 'HIDDEN');
    }
};

const getNoteInfo = (noteId: string, router: Router, routerParams: any, noteService: NoteService, userService: UserService): Promise<boolean | UrlTree> => {
    let noteState = '';

    return noteService.getNoteInfo(noteId)
        .then(note => {
            if (note.shortURLPath) {
                if (routerParams['custom-url'] !== note.shortURLPath) {
                    return router.createUrlTree(['/n', note.shortURLPath]);
                }
            } else if (routerParams['custom-url']) {
                return router.createUrlTree(['/404']);
            }

            if (note.urlName && !note.shortURLPath && !routerParams['custom-url']) {
                return true;
            }

            noteState = note.state;

            if (note.parentPostId && note.parentPostId.length > 0) {
                return noteService.getNoteInfo(note.parentPostId.toString(), true)
                    .then(() => {
                        return router.createUrlTree(['/thread', note.parentPostId], { queryParams: { t: noteId } });
                    })
                    .catch(() => {
                        console.log(`Parent post ${note.parentPostId} doesn't exist. Proceeding with current note.`);
                        return checkPermissions(note, noteState, userService);
                    });
            }

            if (note.childPosts && note.childPosts.length > 0) {
                return router.createUrlTree(['/thread', note.id], { queryParams: { t: note.id } });
            }

            return checkPermissions(note, noteState, userService);
        })
        .catch(err => {
            console.error('Error in getNoteInfo, redirecting to 404', err);
            return router.createUrlTree(['/404']);
        });
};


export const NoteDraftGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const noteService = inject(NoteService);
    const userService = inject(UserService);
    const router = inject(Router);

    const routerParams = route.params;

    if (routerParams['custom-url']) {
        const customUrl = routerParams['custom-url'];

        return noteService.getNoteIdFromCustomUrl(customUrl).then(
            result => {
                if (!result || !result.post || !result.post.id) {
                    console.error('No custom url found or empty result, redirect to 404. Result was:', result);
                    return router.createUrlTree(['/404']);
                }
                return getNoteInfo(result.post.id, router, routerParams, noteService, userService);
            },
            err => {
                console.error('Error fetching custom url, redirect to 404', err);
                return router.createUrlTree(['/404']);
            }
          )
          .catch(err => {
            console.error('Unexpected error during custom url check, redirect to 404', err);
            return router.createUrlTree(['/404']);
          });

    } else {
        return getNoteInfo(routerParams['id'], router, routerParams, noteService, userService);
    }
};